/* assets/loox-reviews.js
   Custom Loox reviews using Loox Storefront API.
   - Real server-side pagination — only fetches on user click.
   - Simple summary: average rating + total review count.
   - JS-managed masonry: explicit column containers, new cards append
     to the currently-shortest column (so Load More lands at the bottom). */
(function () {
  'use strict';

  const TRUNCATE_CHARS = 220;
  const VISIBLE_BATCH = 10;

  function columnCountFor(width) {
    if (width >= 1200) return 4;
    if (width >= 640) return 3;  // tablet
    return 2;                    // mobile (<640px)
  }

  function init(section) {
    if (section.__looxInit) return;
    section.__looxInit = true;

    const shop = section.dataset.shop;
    const productId = section.dataset.productId;
    const productHandle = section.dataset.productHandle;
    const publicStoreId = section.dataset.publicStoreId || '';
    const endpointTemplate = section.dataset.endpoint
      || 'https://storefront-api.loox.io/storefront/v1/store/{public_store_id}/product-reviews?product_id={product_id}';
    const ratingOverride = parseFloat(section.dataset.ratingOverride);
    const hasRatingOverride = !isNaN(ratingOverride) && ratingOverride > 0;
    const reviewsCountOverrideRaw = (section.dataset.reviewsCountOverride || '').trim();
    const reviewsCountOverride = reviewsCountOverrideRaw ? parseInt(reviewsCountOverrideRaw, 10) : null;
    const hasCountOverride = reviewsCountOverride != null && !isNaN(reviewsCountOverride);
    const filterToProduct = section.dataset.filterProduct === '1';
    const currentProductId = String(productId || '');

    const grid = section.querySelector('[data-loox-grid]');
    const summary = section.querySelector('[data-loox-summary]');
    const moreWrap = section.querySelector('[data-loox-more-wrap]');
    const moreBtn = section.querySelector('[data-loox-more]');
    const progress = section.querySelector('[data-loox-progress]');
    const stateLoading = section.querySelector('[data-loox-state="loading"]');
    const stateEmpty = section.querySelector('[data-loox-state="empty"]');
    const stateError = section.querySelector('[data-loox-state="error"]');

    if (!publicStoreId) {
      console.error('[loox-reviews] missing publicStoreId — set it in the section settings.');
      if (stateLoading) stateLoading.hidden = true;
      if (stateError) stateError.hidden = false;
      return;
    }

    const initialUrl = endpointTemplate
      .replace('{public_store_id}', encodeURIComponent(publicStoreId))
      .replace('{shop}', encodeURIComponent(shop))
      .replace('{product_id}', encodeURIComponent(productId))
      .replace('{product_handle}', encodeURIComponent(productHandle));

    const state = {
      all: [],
      shown: 0,
      totalCount: 0,
      hasMore: false,
      nextUrl: null,
      loading: false,
      columns: [],     // [HTMLElement, ...]
      columnCount: 0
    };

    // ── Initial load ─────────────────────────────────────────
    fetchPage(initialUrl).then((parsed) => {
      if (!parsed || !parsed.reviews.length) {
        stateLoading.hidden = true;
        stateEmpty.hidden = false;
        return;
      }
      const filtered = applyProductFilter(parsed.reviews);
      state.all = filtered;
      state.totalCount = parsed.pagination.total || parsed.reviews.length;
      state.hasMore = !!parsed.pagination.hasMore;
      state.nextUrl = parsed.pagination.nextPageUrl || null;
      state.shown = Math.min(VISIBLE_BATCH, state.all.length);

      stateLoading.hidden = true;
      summary.hidden = false;

      // If filter is on and the first page yielded nothing matching, keep
      // fetching pages until we get at least one match (or run out).
      if (filterToProduct && !state.all.length && state.hasMore && state.nextUrl) {
        autoFetchUntilMatches();
      }

      renderSummary();
      syncHeroCount(displayedTotal());
      repaint();
    }).catch((err) => {
      console.error('[loox-reviews] initial fetch failed:', err, 'url:', initialUrl);
      stateLoading.hidden = true;
      stateError.hidden = false;
    });

    // Filter helper: returns only reviews whose product.id matches the current
    // page's product, when the section is configured to.
    function applyProductFilter(reviews) {
      if (!filterToProduct || !currentProductId) return reviews;
      return reviews.filter((r) => r.product && String(r.product.id) === currentProductId);
    }

    // The number shown as "Based on X reviews" in the summary.
    // Priority: explicit override > filtered loaded count (when filter is on) > Loox total.
    function displayedTotal() {
      if (hasCountOverride) return reviewsCountOverride;
      if (filterToProduct) return state.all.length;
      return state.totalCount;
    }

    // Keep fetching pages until at least one matching review is loaded, or
    // we run out of pages. Capped to avoid runaway calls.
    function autoFetchUntilMatches(maxPages) {
      const cap = Math.max(1, maxPages || 6);
      let fetched = 0;
      const step = () => {
        if (fetched >= cap) return;
        if (!state.hasMore || !state.nextUrl) return;
        if (state.all.length > 0) return;
        fetched++;
        fetchPage(state.nextUrl).then((parsed) => {
          if (!parsed) return;
          const filtered = applyProductFilter(parsed.reviews);
          state.all = state.all.concat(filtered);
          state.hasMore = !!parsed.pagination.hasMore;
          state.nextUrl = parsed.pagination.nextPageUrl || null;
          state.shown = Math.min(VISIBLE_BATCH, state.all.length);
          renderSummary();
          repaint();
          step();
        }).catch(() => {});
      };
      step();
    }

    // ── Load more (fetches on demand) ────────────────────────
    moreBtn.addEventListener('click', () => {
      const list = filteredList();
      const prevShown = state.shown;

      // First, reveal more from cached set (append-only, no rebuild)
      if (state.shown < list.length) {
        const nextShown = Math.min(state.shown + VISIBLE_BATCH, list.length);
        appendCards(list.slice(prevShown, nextShown));
        state.shown = nextShown;
        updateProgressAndMore(list);
        return;
      }

      // Else fetch next page(s) from Loox. When the product filter is on,
      // keep fetching until we get at least one matching new review (or run
      // out of pages) — otherwise a single page might yield zero matches
      // and the user thinks Load More is broken.
      if (state.hasMore && state.nextUrl && !state.loading) {
        state.loading = true;
        moreBtn.classList.add('is-loading');
        moreBtn.disabled = true;

        const tryFetch = (attemptsLeft) => {
          fetchPage(state.nextUrl).then((parsed) => {
            if (!parsed) {
              state.loading = false;
              moreBtn.classList.remove('is-loading');
              moreBtn.disabled = false;
              return;
            }
            const filtered = applyProductFilter(parsed.reviews);
            const beforeLen = state.all.length;
            state.all = state.all.concat(filtered);
            state.hasMore = !!parsed.pagination.hasMore;
            state.nextUrl = parsed.pagination.nextPageUrl || null;

            const gotMatches = state.all.length > beforeLen;
            // If filter is on, no new matches yet, and more pages remain,
            // keep going (up to attempts cap).
            if (filterToProduct && !gotMatches && state.hasMore && state.nextUrl && attemptsLeft > 1) {
              tryFetch(attemptsLeft - 1);
              return;
            }

            state.loading = false;
            moreBtn.classList.remove('is-loading');
            moreBtn.disabled = false;
            const newList = filteredList();
            appendCards(newList.slice(prevShown));
            state.shown = newList.length;
            renderSummary();
            updateProgressAndMore(newList);
          }).catch((err) => {
            console.error('[loox-reviews] paginate fetch failed:', err);
            state.loading = false;
            moreBtn.classList.remove('is-loading');
            moreBtn.disabled = false;
          });
        };
        tryFetch(filterToProduct ? 6 : 1);
      }
    });

    // ── Re-layout on resize when column count changes ────────
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      const want = columnCountFor(window.innerWidth);
      if (want === state.columnCount) return;
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(rebuildLayout, 120);
    });

    // ── Read more toggle (event delegated) ───────────────────
    grid.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-loox-toggle]');
      if (!toggle) return;
      const card = toggle.closest('.gl-loox__card');
      if (!card) return;
      const body = card.querySelector('[data-loox-body]');
      const isExpanded = card.classList.toggle('is-expanded');
      const shortEl = body.querySelector('[data-loox-short]');
      const fullEl = body.querySelector('[data-loox-full]');
      if (shortEl) shortEl.hidden = isExpanded;
      if (fullEl) fullEl.hidden = !isExpanded;
      toggle.textContent = isExpanded ? 'Show less' : 'Read more';
    });

    function filteredList() {
      // Loox returns reviews newest-first; we trust that order.
      return state.all;
    }

    /* ── Column management ────────────────────────────────
       Layout strategy:
       - grid contains N column divs.
       - To append: each new card goes into the *currently shortest* column,
         so new cards land at the bottom of the masonry.
       - On filter change or column-count change we wipe and rebuild.
    */
    function ensureColumns() {
      const want = columnCountFor(window.innerWidth);
      if (want === state.columnCount && state.columns.length === want) return;
      state.columnCount = want;
      grid.innerHTML = '';
      state.columns = [];
      for (let i = 0; i < want; i++) {
        const col = document.createElement('div');
        col.className = 'gl-loox__col';
        grid.appendChild(col);
        state.columns.push(col);
      }
    }

    function shortestColumn() {
      let target = state.columns[0];
      let min = target.offsetHeight;
      for (let i = 1; i < state.columns.length; i++) {
        const h = state.columns[i].offsetHeight;
        if (h < min) { target = state.columns[i]; min = h; }
      }
      return target;
    }

    function appendCards(reviews) {
      ensureColumns();
      // Insert one-by-one so each card's actual rendered height influences
      // where the next card goes — gives a balanced fill.
      reviews.forEach((r) => shortestColumn().appendChild(cardEl(r)));
    }

    // Full rebuild: clears columns and re-distributes all visible cards.
    // Used on filter change and on resize when column count changes.
    function rebuildLayout() {
      const list = filteredList();
      // Force fresh columns even if count matched
      state.columnCount = 0;
      ensureColumns();

      if (!list.length) {
        stateEmpty.hidden = false;
        updateProgressAndMore(list);
        return;
      }
      stateEmpty.hidden = true;

      const visibleCount = Math.min(state.shown, list.length);
      appendCards(list.slice(0, visibleCount));
      updateProgressAndMore(list);
    }

    function updateProgressAndMore(list) {
      const visibleCount = Math.min(state.shown, list.length);
      if (progress) {
        if (filterToProduct) {
          // We can't know the true product-specific total without exhausting
          // the API, so just show what's loaded matching this product.
          const suffix = state.hasMore ? '+' : '';
          progress.textContent = 'Showing ' + visibleCount + ' review' + (visibleCount === 1 ? '' : 's') + ' for this product' + suffix;
        } else {
          progress.textContent = 'Showing ' + visibleCount + ' of ' + state.totalCount + ' reviews';
        }
      }
      const moreAvailable = visibleCount < list.length || state.hasMore;
      moreWrap.hidden = !moreAvailable;
    }

    // Back-compat alias for initial render + filter change
    function repaint() { rebuildLayout(); }

    function renderSummary() {
      const loaded = state.all;
      let avg;
      if (hasRatingOverride) {
        avg = ratingOverride;
      } else {
        const sum = loaded.reduce((acc, r) => acc + r.rating, 0);
        avg = loaded.length ? (sum / loaded.length) : 0;
      }
      const shownTotal = displayedTotal();
      summary.querySelector('[data-loox-avg]').textContent = avg.toFixed(1);
      summary.querySelector('[data-loox-avg-stars]').innerHTML = starsHTML(avg);
      summary.querySelector('[data-loox-count]').textContent =
        'Based on ' + formatCount(shownTotal) + ' ' + (shownTotal === 1 ? 'review' : 'reviews');
    }
  }

  // ── Fetch + normalize ──────────────────────────────────────
  function fetchPage(url) {
    return fetch(url, { credentials: 'omit', headers: { 'Accept': 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then((data) => ({
        reviews: normalizeReviews(data),
        pagination: data && data.pagination ? data.pagination : {}
      }));
  }

  function normalizeReviews(data) {
    let list = [];
    if (Array.isArray(data)) list = data;
    else if (data && Array.isArray(data.reviews)) list = data.reviews;
    else if (data && data.widget && Array.isArray(data.widget.reviews)) list = data.widget.reviews;
    else if (data && Array.isArray(data.data)) list = data.data;

    return list.map((r) => {
      const photos = []
        .concat(r.photos || [])
        .concat(r.images || [])
        .concat(r.media || [])
        .map((p) => {
          if (typeof p === 'string') return p;
          if (!p) return null;
          if (p.type && p.type !== 'photo') return null;
          return p.url || p.src || p.thumbnail || p.full || null;
        })
        .filter(Boolean);

      const rating = Number(r.rating || r.score || r.stars || 0) || 0;
      const dateRaw = r.date || r.createdAt || r.created_at || r.published_at;
      const date = dateRaw ? new Date(dateRaw) : null;
      const reviewerName = (
        (r.reviewer && r.reviewer.name) ||
        r.author || r.name || r.customer || 'Verified customer'
      );

      return {
        id: r.id || '',
        rating: Math.max(0, Math.min(5, Math.round(rating))),
        title: (r.title || r.heading || '').trim(),
        body: (r.body || r.text || r.content || r.review || '').trim(),
        photos: photos,
        author: String(reviewerName).trim(),
        date: date && !isNaN(date.getTime()) ? date : null,
        verified: r.verified !== false
      };
    });
  }

  // ── Card: image only if present, else text-only ────────────
  function cardEl(r) {
    const card = document.createElement('article');
    card.className = 'gl-loox__card';
    card.setAttribute('role', 'listitem');
    if (!r.photos.length) card.classList.add('gl-loox__card--text');

    // Image: only if the review actually has one
    if (r.photos.length) {
      const media = document.createElement('div');
      media.className = 'gl-loox__card-media';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = '';
      img.src = r.photos[0];
      media.appendChild(img);
      card.appendChild(media);
    }

    const body = document.createElement('div');
    body.className = 'gl-loox__card-content';

    // Stars
    const head = document.createElement('div');
    head.className = 'gl-loox__card-head';
    head.innerHTML =
      '<div class="gl-loox__card-stars" aria-label="' + r.rating + ' out of 5">' + starsHTML(r.rating) + '</div>';
    body.appendChild(head);

    // Title (if Loox returned one — rare for this store)
    if (r.title) {
      const h = document.createElement('h3');
      h.className = 'gl-loox__card-title';
      h.textContent = r.title;
      body.appendChild(h);
    }

    // Body with read-more
    const text = r.body;
    if (text) {
      const isLong = text.length > TRUNCATE_CHARS;
      const short = isLong ? text.slice(0, TRUNCATE_CHARS).trim() + '…' : text;

      const p = document.createElement('p');
      p.className = 'gl-loox__card-body';
      p.setAttribute('data-loox-body', '');
      const sShort = document.createElement('span');
      sShort.setAttribute('data-loox-short', '');
      sShort.textContent = short;
      p.appendChild(sShort);
      if (isLong) {
        const sFull = document.createElement('span');
        sFull.setAttribute('data-loox-full', '');
        sFull.hidden = true;
        sFull.textContent = text;
        p.appendChild(sFull);
      }
      body.appendChild(p);

      if (isLong) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'gl-loox__card-toggle';
        btn.setAttribute('data-loox-toggle', '');
        btn.textContent = 'Read more';
        body.appendChild(btn);
      }
    }

    // Footer — author · date · verified
    const foot = document.createElement('footer');
    foot.className = 'gl-loox__card-foot';
    const authorLine = document.createElement('div');
    authorLine.className = 'gl-loox__card-author-line';
    const author = document.createElement('span');
    author.className = 'gl-loox__card-author';
    author.textContent = r.author;
    authorLine.appendChild(author);
    if (r.verified) {
      const vb = document.createElement('span');
      vb.className = 'gl-loox__card-verified';
      vb.title = 'Verified Buyer';
      vb.textContent = '✓';
      authorLine.appendChild(vb);
    }
    foot.appendChild(authorLine);
    if (r.date) {
      const date = document.createElement('time');
      date.className = 'gl-loox__card-date';
      date.textContent = formatDate(r.date);
      foot.appendChild(date);
    }
    body.appendChild(foot);

    card.appendChild(body);
    return card;
  }

  function starsHTML(rating) {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    let html = '';
    for (let i = 1; i <= 5; i++) {
      let cls = 'gl-loox__star';
      if (i <= full) cls += ' is-full';
      else if (i === full + 1 && half) cls += ' is-half';
      html += '<span class="' + cls + '" aria-hidden="true">★</span>';
    }
    return html;
  }

  function formatDate(d) {
    try { return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }); }
    catch (e) { return ''; }
  }

  function formatCount(n) {
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return String(n);
  }

  // Update any hero (or other) count elements to match Loox's true total.
  function syncHeroCount(total) {
    document.querySelectorAll('[data-loox-hero-count]').forEach((el) => {
      el.textContent = formatCount(total);
    });
  }

  // Smooth scroll for the hero link (and anything else pointing at the section).
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href="#pdp-loox-reviews"]');
    if (!link) return;
    const target = document.getElementById('pdp-loox-reviews');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (history && history.replaceState) {
      history.replaceState(null, '', '#pdp-loox-reviews');
    }
  });

  // Lazy-init: defer fetch + render until the section is near the viewport.
  // Loox reviews are below the fold and were a known LCP/CLS offender — no
  // reason to pay for them on first paint.
  function lazyInit(section) {
    if (!('IntersectionObserver' in window)) {
      init(section);
      return;
    }
    if (section.dataset.looxLazyArmed === '1') return;
    section.dataset.looxLazyArmed = '1';
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          obs.unobserve(entry.target);
          init(entry.target);
        }
      });
    }, { rootMargin: '600px 0px' });
    io.observe(section);
  }

  function bootstrap() {
    document.querySelectorAll('[data-loox-section]').forEach(lazyInit);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootstrap);
  else bootstrap();
  document.addEventListener('shopify:section:load', (e) => {
    const sec = e.target.querySelector('[data-loox-section]');
    if (sec) lazyInit(sec);
  });
})();
